import {
  AppstoreOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo, useRef } from 'react';
import type { LineageEdge, LineageFieldTrace, LineageNode, LineageProjection, LineageStage } from './lineageDemoApi';

const stages: Array<{ id: LineageStage; label: string }> = [
  { id: 'source', label: 'SOURCES' },
  { id: '00', label: '00 EXTRACT' },
  { id: '01', label: '01 TRANSFORM' },
  { id: '02', label: '02 MODEL' },
  { id: '03', label: '03 APPS' },
  { id: 'analytic', label: 'ANALYTICS' },
];

const laneWidth = 124;
const nodeWidth = 112;
const nodeHeight = 78;
const rowHeight = 112;

const provenanceClass = (value: LineageNode['provenance'] | LineageEdge['provenance']) => value ?? 'declared-demo';
const provenanceLabel = (value: LineageNode['provenance']) => value === 'observed' ? 'Scanned' : value === 'parsed' ? 'Parsed' : 'Simulated';

const NodeIcon: React.FC<{ node: LineageNode }> = ({ node }) => {
  if (node.type === 'source') return <DatabaseOutlined />;
  if (node.type === 'qvd') return <DatabaseOutlined />;
  if (node.type === 'app') return <FileTextOutlined />;
  return <BarChartOutlined />;
};

interface PositionedNode { node: LineageNode; x: number; y: number }

const edgePath = (from: PositionedNode, to: PositionedNode) => {
  const startX = from.x + nodeWidth;
  const startY = from.y + nodeHeight / 2;
  const endX = to.x;
  const endY = to.y + nodeHeight / 2;
  const curve = Math.max(8, (endX - startX) * .45);
  return `M ${startX} ${startY} C ${startX + curve} ${startY}, ${endX - curve} ${endY}, ${endX} ${endY}`;
};

export const LineageGraph: React.FC<{
  projection: LineageProjection;
  selectedNodeId: string;
  selectedEdgeId: string | null;
  fieldTrace: LineageFieldTrace | null;
  depth: 'resource' | 'table' | 'field';
  onSelectNode: (node: LineageNode) => void;
  onSelectEdge: (edge: LineageEdge) => void;
}> = ({ projection, selectedNodeId, selectedEdgeId, fieldTrace, depth, onSelectNode, onSelectEdge }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const layout = useMemo(() => {
    const stageMap = new Map<LineageStage, LineageNode[]>();
    stages.forEach(stage => stageMap.set(stage.id, []));
    projection.nodes.forEach(node => stageMap.get(node.stage)?.push(node));
    stageMap.forEach(laneNodes => laneNodes.sort((left, right) => Number(left.metadata.displayOrder ?? 999) - Number(right.metadata.displayOrder ?? 999) || left.label.localeCompare(right.label)));
    const maxRows = Math.max(1, ...Array.from(stageMap.values()).map(nodes => nodes.length));
    const positions = new Map<string, PositionedNode>();
    stages.forEach((stage, laneIndex) => {
      const laneNodes = stageMap.get(stage.id) ?? [];
      const laneOffset = (maxRows - laneNodes.length) * rowHeight / 2;
      laneNodes.forEach((node, rowIndex) => positions.set(node.id, {
        node,
        x: laneIndex * laneWidth + 12,
        y: 56 + laneOffset + rowIndex * rowHeight,
      }));
    });
    return { positions, height: Math.max(560, 110 + maxRows * rowHeight), width: stages.length * laneWidth };
  }, [projection.nodes]);

  useEffect(() => {
    if (projection.mode !== 'node-explore') return;
    const anchor = layout.positions.get(projection.anchorNodeId);
    const container = scrollRef.current;
    if (!anchor || !container) return;
    container.scrollTop = Math.max(0, anchor.y + nodeHeight / 2 - container.clientHeight / 2);
  }, [layout, projection.anchorNodeId, projection.mode]);

  const tracedNodeIds = useMemo(() => new Set(fieldTrace?.nodeIds ?? []), [fieldTrace]);
  const tracedEdgeIds = useMemo(() => new Set(fieldTrace?.edgeIds ?? []), [fieldTrace]);
  const hasTrace = Boolean(fieldTrace);
  const isSingleField = Boolean(fieldTrace?.selectedField);

  return (
    <div ref={scrollRef} className="lineage-graph-scroll" aria-label="Qlik lineage topology">
      <div className="lineage-graph-canvas" style={{ width: layout.width, height: layout.height }}>
        {stages.map((stage, index) => (
          <div className="lineage-lane" key={stage.id} style={{ left: index * laneWidth, width: laneWidth }}>
            <span>{stage.label}</span>
          </div>
        ))}
        <svg className="lineage-edge-layer" width={layout.width} height={layout.height} aria-hidden="false">
          <defs>
            <marker id="lineage-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
            <marker id="lineage-arrow-traced" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>
          {projection.edges.map(edge => {
            const from = layout.positions.get(edge.source);
            const to = layout.positions.get(edge.target);
            if (!from || !to) return null;
            const path = edgePath(from, to);
            const traced = tracedEdgeIds.has(edge.id);
            return (
              <g key={edge.id} className={`lineage-edge provenance-${provenanceClass(edge.provenance)} ${traced ? 'is-traced' : hasTrace ? 'is-context' : ''} ${isSingleField && traced ? 'is-field-focus' : ''} ${selectedEdgeId === edge.id ? 'is-selected' : ''}`}>
                <path className="lineage-edge-hit" d={path} tabIndex={0} role="button" aria-label={`Inspect ${edge.relation} relationship`} onClick={() => onSelectEdge(edge)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') onSelectEdge(edge); }} />
                {traced ? <path className="lineage-edge-glow" d={path} /> : null}
                <path className="lineage-edge-line" d={path} markerEnd={traced ? 'url(#lineage-arrow-traced)' : 'url(#lineage-arrow)'} />
              </g>
            );
          })}
        </svg>
        {Array.from(layout.positions.values()).map(({ node, x, y }) => {
          const selected = node.id === selectedNodeId;
          const anchor = node.id === projection.anchorNodeId;
          const selectedApp = node.id === projection.selectedAppId;
          const traced = tracedNodeIds.has(node.id);
          const tracedFields = fieldTrace?.nodeFields[node.id] ?? [];
          return (
            <button
              key={node.id}
              type="button"
              className={`lineage-node lineage-node-${node.type} provenance-${provenanceClass(node.provenance)} ${traced ? 'is-traced' : hasTrace ? 'is-context' : ''} ${isSingleField && traced ? 'is-field-focus' : ''} ${selected ? 'is-selected' : ''} ${anchor ? 'is-anchor' : ''}`}
              style={{ left: x, top: y, width: nodeWidth, height: nodeHeight }}
              onClick={() => onSelectNode(node)}
              aria-pressed={selected}
              aria-label={`Inspect ${node.label}`}
            >
              <span className="lineage-node-title"><NodeIcon node={node} /><strong>{node.label}</strong></span>
              <small>{node.subtitle}</small>
              <span className="lineage-node-footer">
                {selectedApp ? <em>Selected App</em> : node.role === 'shared-consumer' ? <em className="is-shared">Shared consumer</em> : <span><AppstoreOutlined /> {node.fields.length} fields</span>}
                {depth === 'field' && tracedFields.length ? <code title={tracedFields.join(', ')}>{tracedFields[0]}{tracedFields.length > 1 ? ` +${tracedFields.length - 1}` : ''}</code> : null}
              </span>
              <i className={`lineage-node-provenance is-${provenanceClass(node.provenance)}`}>{provenanceLabel(node.provenance)}</i>
            </button>
          );
        })}
      </div>
    </div>
  );
};
